import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { CommentsService } from '../../services/comments.service';
import { UserInfo } from '../comments.models';

interface MentionUser extends UserInfo {
  mentionText?: string;
}

@Component({
  selector: 'app-mention-input',
  standalone: true,
  imports: [CommonModule, FormsModule, OverlayPanelModule],
  templateUrl: './mention-input.component.html',
  styleUrl: './mention-input.component.scss',
})
export class MentionInputComponent implements OnInit, AfterViewInit {
  @Input() value: string = '';
  @Output() valueChange = new EventEmitter<string>();

  @Input() placeholder: string = 'Write a comment...';
  @Input() rows: number = 3;
  @Input() autoResize: boolean = true;

  @ViewChild('textarea') textarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('mentionPanel') mentionPanel!: OverlayPanel;

  mentionSearch: string = '';
  filteredUsers: MentionUser[] = [];
  showMentionDropdown: boolean = false;
  selectedIndex: number = 0;
  mentionStartPos: number = -1;

  // Map to store userId -> username for display
  mentionMap = new Map<number, string>();

  constructor(private commentsService: CommentsService) {}

  ngOnInit(): void {
    // Users are loaded lazily when @ is typed via searchUsers() → getAllUsers() (cached)
  }

  ngAfterViewInit(): void {
    // Add keyboard event listeners
    const el = this.textarea.nativeElement;

    el.addEventListener('keydown', (e: KeyboardEvent) => {
      if (this.showMentionDropdown) {
        this.handleMentionNavigation(e);
      }
    });
  }

  onInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    const text = target.value;
    const cursorPos = target.selectionStart || 0;

    this.value = text;
    this.valueChange.emit(text);

    // Check for @ mention trigger
    this.checkForMention(text, cursorPos);
  }

  private checkForMention(text: string, cursorPos: number): void {
    // Find the last @ before cursor
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex === -1) {
      this.closeMentionDropdown();
      return;
    }

    // Check if there's a space between @ and cursor (mention ended)
    const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
    if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
      this.closeMentionDropdown();
      return;
    }

    // Get the search query
    this.mentionSearch = textAfterAt;
    this.mentionStartPos = lastAtIndex;

    // Search for users
    this.commentsService.searchUsers(this.mentionSearch).subscribe((users) => {
      this.filteredUsers = users.map((user) => ({
        ...user,
        mentionText: `${user.first_name} ${user.last_name}`,
      }));

      if (this.filteredUsers.length > 0) {
        this.selectedIndex = 0;
        this.showMentionDropdown = true;
        this.showDropdownAtCursor();
      } else {
        this.closeMentionDropdown();
      }
    });
  }

  private showDropdownAtCursor(): void {
    if (this.mentionPanel && this.textarea) {
      const event = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });

      this.mentionPanel.show(event, this.textarea.nativeElement);
    }
  }

  private handleMentionNavigation(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredUsers.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        break;
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        if (this.filteredUsers[this.selectedIndex]) {
          this.selectUser(this.filteredUsers[this.selectedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeMentionDropdown();
        break;
    }
  }

  selectUser(user: MentionUser): void {
    if (this.mentionStartPos === -1) return;

    const textarea = this.textarea.nativeElement;
    const cursorPos = textarea.selectionStart || 0;

    // Replace @search with @userId
    const beforeMention = this.value.substring(0, this.mentionStartPos);
    const afterCursor = this.value.substring(cursorPos);

    // Store the mapping for display
    this.mentionMap.set(user.id, `${user.first_name} ${user.last_name}`);

    // Insert mention as @userId
    const newValue = `${beforeMention}@${user.id} ${afterCursor}`;
    const newCursorPos = this.mentionStartPos + user.id.toString().length + 2; // +2 for @ and space

    this.value = newValue;
    this.valueChange.emit(newValue);

    // Set cursor position after mention
    setTimeout(() => {
      textarea.value = newValue;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);

    this.closeMentionDropdown();
  }

  private closeMentionDropdown(): void {
    this.showMentionDropdown = false;
    if (this.mentionPanel) {
      this.mentionPanel.hide();
    }
    this.mentionStartPos = -1;
    this.selectedIndex = 0;
  }

  getUserInitials(user: UserInfo): string {
    const first = user.first_name?.charAt(0) || '';
    const last = user.last_name?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  }

  /**
   * Get mentioned user IDs from the current text
   */
  getMentionedUserIds(): number[] {
    const mentionPattern = /@(\d+)/g;
    const userIds: number[] = [];
    let match;

    while ((match = mentionPattern.exec(this.value)) !== null) {
      const userId = parseInt(match[1], 10);
      if (!isNaN(userId)) {
        userIds.push(userId);
      }
    }

    return [...new Set(userIds)]; // Remove duplicates
  }
}
